import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

const AgeVerificationModal = () => {
  const [showModal, setShowModal] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Skip age verification for admin and dev routes
    const isAdminRoute = location.pathname.startsWith('/admin') || 
                         location.pathname.startsWith('/dev');
    
    if (isAdminRoute) {
      setShowModal(false);
      document.body.style.overflow = 'auto';
      return;
    }
    
    // Check if user has already verified age
    const ageVerified = localStorage.getItem('amino_age_verified');
    if (!ageVerified) {
      setShowModal(true);
      // Prevent scrolling when modal is open
      document.body.style.overflow = 'hidden';
    }
  }, [location.pathname]);

  const handleVerify = () => {
    localStorage.setItem('amino_age_verified', 'true');
    localStorage.setItem('amino_age_verified_date', new Date().toISOString());
    setIsExiting(true);
    setTimeout(() => {
      setShowModal(false);
      document.body.style.overflow = 'auto';
    }, 300);
  };

  const handleDecline = () => {
    // Redirect to Google or show message
    window.location.href = 'https://www.google.com';
  };

  if (!showModal) return null;

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-300 ${
        isExiting ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      
      {/* Modal */}
      <div 
        className={`relative bg-gradient-to-b from-[#1a0a2e] to-[#0f0419] rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden transform transition-all duration-300 ${
          isExiting ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
      >
        {/* Gold accent line */}
        <div className="h-1 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400" />
        
        {/* Content */}
        <div className="p-8">
          {/* Logo/Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-600/20 to-purple-800/20 flex items-center justify-center shadow-lg shadow-purple-500/30 p-2">
              <img 
                src="/favicon.png" 
                alt="AMINO-CHAIN" 
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          
          {/* Title */}
          <h2 className="text-2xl font-bold text-center text-white mb-2">
            Age Verification Required
          </h2>
          
          {/* Subtitle */}
          <p className="text-center text-purple-200 mb-6">
            You must be 21 years or older to enter this website
          </p>
          
          {/* Warning Box */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-200">
                <p className="font-semibold mb-1">Research Products Only</p>
                <p className="text-amber-200/80">
                  All products on this site are sold exclusively for laboratory research purposes. 
                  They are NOT intended for human consumption, therapeutic use, or any diagnostic procedures.
                </p>
              </div>
            </div>
          </div>
          
          {/* Verification Text */}
          <p className="text-center text-gray-400 text-sm mb-6">
            By clicking "I Am 21 or Older", you confirm that you are at least 21 years of age 
            and agree to our <a href="/terms-conditions" className="text-purple-400 hover:text-purple-300 underline">Terms & Conditions</a>.
          </p>
          
          {/* Buttons */}
          <div className="flex flex-col gap-3">
            <button
              onClick={handleVerify}
              className="w-full py-4 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 transform hover:-translate-y-0.5"
            >
              I Am 21 or Older - Enter Site
            </button>
            
            <button
              onClick={handleDecline}
              className="w-full py-3 bg-transparent border border-gray-600 hover:border-gray-500 text-gray-400 hover:text-gray-300 font-medium rounded-xl transition-all duration-200"
            >
              I Am Under 21 - Exit
            </button>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-black/30 px-8 py-4 border-t border-purple-900/50">
          <p className="text-center text-xs text-gray-500">
            AMINO-CHAIN • Premium Research Peptides<br />
            <span className="text-amber-500/70">FOR RESEARCH USE ONLY • NOT FOR HUMAN CONSUMPTION</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AgeVerificationModal;
